/* Scroll-snap carousel helper.
   Replaces Swiper for carousels that only needed a scroller plus autoplay,
   an optional seamless loop, and optional dots. Layout is pure CSS - see the
   [data-carousel] block in gigli-carousel.css. This file only adds behaviour.

   Seamless loop: the slide set is duplicated ONCE, so the track holds
   [A B C][A' B' C']. When the scroll position passes one set-width we subtract
   a set-width instantly. A and A' are pixel-identical, so the jump is invisible
   and the DOM stays a fixed size - no cloning or removing slides while it runs.
   The wrap is forward-only, which is the direction autoplay travels; dragging
   back simply stops at the first slide.

   Options, read from the container:
     data-carousel-autoplay="2500"   ms between advances; absent = no autoplay
     data-carousel-loop              duplicate the set for a seamless wrap
     data-carousel-keep-autoplay     keep autoplaying after the user interacts
                                     (Swiper's disableOnInteraction: false)
     data-carousel-dots="<selector>" indicator container. If it already has element
                                     children they are used as-is (see the reviews
                                     progress bar); otherwise dots are generated.
     data-carousel-dots-active="cls"  class toggled on the active indicator
                                     (default "is-active")                      */
const gigliCarouselSeen = new WeakSet();

function gigliCarouselInit() {
  document.querySelectorAll("[data-carousel]").forEach((root) => {
    if (gigliCarouselSeen.has(root)) return;
    const track = root.querySelector(".swiper-wrapper");
    if (!track) return;
    const originals = Array.prototype.slice.call(track.children);
    if (originals.length < 2) return;
    gigliCarouselSeen.add(root);

    const loop = root.hasAttribute("data-carousel-loop"),
      delay = parseInt(root.getAttribute("data-carousel-autoplay") || "0", 10),
      keepAutoplay = root.hasAttribute("data-carousel-keep-autoplay"),
      dotsSel = root.getAttribute("data-carousel-dots"),
      reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

    let setWidth = 0;
    const measure = () => {
      setWidth = loop ? track.scrollWidth / 2 : 0;
    };

    const step = () => {
      const first = track.children[0];
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      return first.getBoundingClientRect().width + gap;
    };
    /* prefers-reduced-motion suppresses the ANIMATION, not the carousel. Treating it as
       "switch autoplay off" is what the first version did, and it meant the sliders sat
       motionless for anyone with Reduce Motion or Windows' "show animations" turned off -
       with no other symptom to explain it. Swiper ignored the setting outright; jumping
       straight to the next slide keeps the content advancing while honouring the request
       not to animate. */
    const behavior = () => (reduced.matches ? "auto" : "smooth");
    const advance = () => {
      const max = root.scrollWidth - root.clientWidth;
      if (!loop && root.scrollLeft >= max - 1) root.scrollTo({ left: 0, behavior: behavior() });
      else root.scrollBy({ left: step(), behavior: behavior() });
    };

    let timer = null;
    const start = () => {
      if (timer || !delay) return;
      timer = setInterval(advance, delay);
    };
    const stop = () => {
      (clearInterval(timer), (timer = null));
    };

    /* Clones carry real <img> elements. Building them at page load made the browser
       fetch every duplicated slide immediately - measured as 22 extra image requests
       on the homepage, because a freshly inserted lazy <img> is evaluated against the
       layout as it stands at insertion time rather than staying deferred. So nothing
       is cloned, and autoplay does not run, until the carousel nears the viewport. */
    const activate = () => {
      if (loop) {
        Array.prototype.forEach.call(originals, (el) => {
          const clone = el.cloneNode(true);
          clone.classList.add("is-carousel-clone");
          clone.setAttribute("aria-hidden", "true");
          clone
            .querySelectorAll("a,button,input,select,textarea,[tabindex]")
            .forEach((f) => f.setAttribute("tabindex", "-1"));
          clone.querySelectorAll("img").forEach((img) => {
            (img.setAttribute("loading", "lazy"), img.setAttribute("decoding", "async"));
          });
          track.appendChild(clone);
        });
        measure();
        if (window.ResizeObserver) new ResizeObserver(measure).observe(track);
        let settle = null;
        const wrap = () => {
          if (setWidth && root.scrollLeft >= setWidth) {
            const b = root.style.scrollBehavior;
            ((root.style.scrollBehavior = "auto"),
              (root.scrollLeft -= setWidth),
              (root.style.scrollBehavior = b));
          }
        };
        root.addEventListener(
          "scroll",
          () => {
            (clearTimeout(settle), (settle = setTimeout(wrap, 120)));
          },
          { passive: true }
        );
      }

      if (delay) {
        /* No hover pausing. Swiper's pauseOnMouseEnter defaults to false and neither slider
           set it, so the original never paused on hover - and adding it meant that on a
           desktop, a cursor left resting anywhere over the carousel stopped autoplay for as
           long as it sat there, which reads as "autoplay is broken".

           focus pausing stays, because a keyboard user tabbing through slides should not have
           them move underneath. focusout matters as much as focusin: without it, focus landing
           on any link inside a slide stopped autoplay permanently. */
        (root.addEventListener("focusin", stop),
          root.addEventListener("focusout", start),
          document.addEventListener("visibilitychange", () =>
            document.hidden ? stop() : start()
          ));
        if (!keepAutoplay) {
          /* Swiper's disableOnInteraction also stopped autoplay for good, but it only ever
             counted gestures its own touch handling recognised as slider drags. These
             listeners sit on the scroll container, so a finger that merely BEGINS a vertical
             page scroll over the carousel counted too, and wheel fired whenever the pointer
             happened to be above it while the page scrolled. On a phone that meant autoplay
             was dead by the time the section reached the viewport.

             So: wheel is gone, and a real touch pauses rather than kills. Autoplay resumes
             once the user has settled, which is what someone watching a review carousel
             expects anyway. */
          let resume = null;
          const pause = () => {
            (stop(), clearTimeout(resume), (resume = setTimeout(start, Math.max(delay, 2500))));
          };
          ["pointerdown", "touchstart"].forEach((e) =>
            root.addEventListener(e, pause, { passive: true })
          );
        }
        start();
      }
    };

    if (window.IntersectionObserver) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            (io.disconnect(), activate());
          });
        },
        { rootMargin: "200px" }
      );
      io.observe(root);
    } else activate();

    if (dotsSel) {
      const host = root.parentElement.querySelector(dotsSel) || document.querySelector(dotsSel);
      if (host) {
        const activeCls = root.getAttribute("data-carousel-dots-active") || "is-active";
        const goTo = (i) => {
          const slide = originals[i];
          if (!slide) return;
          (stop(),
            root.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: behavior() }));
        };

        /* Reuse indicator markup that is already in the section rather than replacing it.
           The reviews slider ships a seven-segment progress bar - styled divs, not buttons,
           and deliberately fewer of them than there are slides. Swiper drove it from a
           slideChange handler that the conversion dropped, which is why it sat stuck on the
           first segment. Generating dots over the top would have thrown the design away. */
        const existing = Array.prototype.filter.call(host.children, (n) => n.nodeType === 1);
        let dots;
        if (existing.length) {
          dots = existing;
          dots.forEach((d, i) => {
            if (d.tagName === "BUTTON") d.addEventListener("click", () => goTo(i));
          });
        } else {
          dots = originals.map((slide, i) => {
            const d = document.createElement("button");
            ((d.type = "button"),
              (d.className = "gigli-carousel__dot"),
              d.setAttribute("aria-label", "Go to slide " + (i + 1)),
              d.addEventListener("click", () => goTo(i)),
              host.appendChild(d));
            return d;
          });
        }

        const sync = () => {
          const s = step() || 1,
            raw = Math.round(root.scrollLeft / s),
            i = ((raw % originals.length) + originals.length) % originals.length,
            /* Spread however many slides there are across however many indicators exist, so
               this works for a dot-per-slide and for the shorter progress bar alike. */
            active = Math.min(dots.length - 1, Math.floor((i / originals.length) * dots.length));
          dots.forEach((d, n) => d.classList.toggle(activeCls, n === active));
        };
        (root.addEventListener("scroll", sync, { passive: true }), sync());
      }
    }
  });
}

(document.readyState === "loading"
  ? document.addEventListener("DOMContentLoaded", gigliCarouselInit)
  : gigliCarouselInit(),
  document.addEventListener("shopify:section:load", function () {
    setTimeout(gigliCarouselInit, 100);
  }));
